import { ReactElement, Fragment } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { IconCloseSmall } from '@components/icons/close-small';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { getDisplayForFilter, getUsedFieldConfig } from './filter-utils';
import { AppliedFilterData, FilterComparer, SelectableFilterConfigField } from './shared-types';

import { FilterActions } from '../table-store';

export const AppliedFilter = ({
  filter,
  config,
  actions,
}: {
  filter: AppliedFilterData;
  config: SelectableFilterConfigField;
  actions: FilterActions;
}) => {
  const { t, lang } = useScopedTranslate({ en: {} });

  if (config.type === 'custom' && 'customAppliedFilter' in config && config.customAppliedFilter) {
    const CustomAppliedFilter = config.customAppliedFilter;
    return <CustomAppliedFilter filter={filter} config={config} actions={actions} />;
  }

  const usedConfig = getUsedFieldConfig(config, filter.field);
  if (!usedConfig) {
    return null;
  }

  const display = getDisplayForFilter(filter, usedConfig, lang);

  return (
    <RenderAppliedFilter
      actions={actions}
      id={filter.id}
      comparer={{
        label: t(`core.comparator.${filter.comparer!}`).replace('...', ''),
        value: filter.comparer!,
      }}
      value={display}
      category={usedConfig.label}
      icon={config.icon}
    />
  );
};

export const RenderAppliedFilter = ({
  id,
  icon,
  category,
  comparer,
  value,
  actions,
}: {
  id: string;
  icon?: ReactElement<IconProps>;
  category: string;
  comparer?: {
    label: string;
    value: FilterComparer;
  };
  value?: string | string[];
  actions: FilterActions;
}) => {
  return (
    <Stack
      orientation="horizontal"
      justify="space-between"
      space={2}
      as="span"
      style={{ minWidth: 0 }}
    >
      <Stack orientation="horizontal" space={1} align="center" as="span" style={{ minWidth: 0 }}>
        {icon}
        <CategoryText text={category} />
        <Text font="semibold14" color="textPrimary" text={<>&bull;</>} />

        {comparer && (
          <Fragment>
            <ComparerText color={colorMap[comparer.value]} text={comparer.label} />
            {value && <Text font="semibold14" color="textPrimary" text={<>&bull;</>} />}
          </Fragment>
        )}

        {value && <ValueText text={Array.isArray(value) ? value.join(', ') : value} />}

        <CloseContainer onClick={() => actions.removeFilter(id)}>
          <IconCloseSmall />
        </CloseContainer>
      </Stack>
    </Stack>
  );
};

const CategoryText = styled(Text).attrs({
  font: 'semibold14',
  color: 'textPrimary',
})({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: '20px',
  flexShrink: 1,
});

const CloseContainer = styled.div({
  height: pxToRem(32),
  width: pxToRem(32),
  marginRight: pxToRem(-8),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ValueText = styled(Text).attrs({
  font: 'semibold14',
  color: 'elementBlue100',
})({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  flexShrink: 50,
});

const ComparerText = styled(Text).attrs({
  font: 'semibold14',
})({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  flexShrink: 10,
});

const colorMap = {
  is: 'elementGreen200',
  eq: 'elementGreen200',
  on: 'elementGreen200',

  any_of: 'elementOrange200',
  contains: 'elementOrange200',
  contains_multi: 'elementOrange200',

  not_is: 'elementRed200',
  none_of: 'elementRed200',
  not_contains: 'elementRed200',

  today: 'elementBlue100',
  yesterday: 'elementBlue100',

  after: 'elementOrange200',
  before: 'elementOrange200',
  between: 'elementOrange200',
  gt: 'elementOrange200',
  is_none: 'elementOrange200',
  is_rated_by: 'elementOrange200',
  lt: 'elementOrange200',
  not_is_rated_by: 'elementOrange200',
  not_starts: 'elementOrange200',
  starts: 'elementOrange200',
} satisfies Record<FilterComparer, Color>;
